import { useAuth0 } from "@auth0/auth0-react";
import Intercom from "@intercom/messenger-js-sdk";
import rg4js from "raygun4js";
import type { ReactNode } from "react";
import Loading from "@/components/Loading";
import ValidateEmail from "@/screens/ValidateEmail";
import ErrorComponent from "./screens/Error";

// When I was first setting up enterprise connections in auth0 I didn't realize that the "connection" name would be case sensitive... -_-
const subdomainMappings = {
  briefpointtest: "BriefpointTest",
  statefarm: "StateFarm",
};

type Props = {
  children: ReactNode;
};

function LoadingComponent() {
  return <Loading className="authLoading mt-3" isLoading />;
}

function fixSubdomain(subdomain: string) {
  const match = subdomainMappings[subdomain as keyof typeof subdomainMappings];

  return match ?? subdomain;
}

function UserAuth({ children }: Props) {
  const { isAuthenticated, loginWithRedirect, isLoading, user } = useAuth0();
  // error from url params
  const urlParams = new URLSearchParams(window.location.search);
  const error = urlParams.get("error");
  const errorDescription = urlParams.get("error_description");
  const IntercomId: string = import.meta.env.VITE_INTERCOM_ID;
  if (error) {
    if (errorDescription === "the connection is not enabled") {
      sessionStorage.setItem("oauthError", error);
    } else {
      if (IntercomId) {
        Intercom({
          app_id: IntercomId,
        });
      }
      return (
        <ErrorComponent>
          <h1 className="display-1 mb-3">401</h1>
          <p className="text-muted">
            Something went wrong with your login. Please contact support at{" "}
            <a href="mailto:support@briefpoint.ai">support@briefpoint.ai</a>.
          </p>
        </ErrorComponent>
      );
    }
  }

  // if Auth0 is loading, wait
  if (isLoading && !isAuthenticated) {
    return <LoadingComponent />;
  }

  // if not authenticated, redirect to login
  if (!isAuthenticated) {
    const baseUrl = window.location.hostname;
    let subdomain: string | undefined = baseUrl.split(".")[0];

    const viteUrl = new URL(import.meta.env.VITE_BASE_URL.split(";")[0]);
    const viteSubdomain = viteUrl.hostname.split(".")[0];

    if (sessionStorage.getItem("oauthError")) {
      rg4js("send", {
        error: new Error(`${subdomain} is not configured in Auth0`),
      });
      subdomain = undefined;
    }

    // don't include error/error_description in redirect otherwise we can get stuck in a loop
    const url = new URL(window.location.href);
    url.searchParams.delete("error");
    url.searchParams.delete("error_description");

    loginWithRedirect({
      authorizationParams: {
        redirect_uri: url.toString(),
        connection:
          !subdomain || subdomain === viteSubdomain
            ? undefined
            : fixSubdomain(subdomain),
      },
    });
  }

  if (user?.email_verified === false) {
    return <ValidateEmail user={user} />;
  }
  return <>{children}</>;
}

export default UserAuth;
